import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Hexagon, ScanBarcode } from "lucide-react";

const LOGO = "/assets/germac-logo.png";
const HERO = "https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxoZWF2eSUyMG1hY2hpbmVyeSUyMHdhcmVob3VzZSUyMHNoZWx2ZXN8ZW58MHx8fHwxNzc5MTkzOTgyfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) { nav("/"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Errore di accesso");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#050505]">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 bg-white p-1.5 flex items-center justify-center rounded-sm">
              <img src={LOGO} alt="GERMAC" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-black tracking-tight uppercase">GERMAC <span className="text-zinc-500 text-sm font-normal">s.r.l.</span></div>
              <div className="text-xs text-zinc-400 uppercase tracking-[0.2em]">Macchine da lavoro</div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#4CAF50]/40 bg-[#4CAF50]/10 text-[#4CAF50] text-xs uppercase tracking-widest">
              <span className="h-1.5 w-1.5 bg-[#4CAF50]" /> Sistema operativo
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-black uppercase leading-[0.95] tracking-tighter max-w-md">
              Controllo<br/>
              <span className="text-[#4CAF50]">totale</span> del<br/>
              tuo magazzino.
            </h1>
            <p className="text-zinc-400 max-w-md text-base">
              Gestione ricambi industriali, carico/scarico con scansione codice a barre, etichette stampabili e tracciabilità completa.
            </p>
          </div>
          <div className="font-mono-data text-xs text-zinc-600">v1.0 — © GERMAC s.r.l.</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="h-12 w-12 bg-white p-1 flex items-center justify-center rounded-sm">
              <img src={LOGO} alt="GERMAC" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-black uppercase">GERMAC <span className="text-zinc-500 text-sm font-normal">s.r.l.</span></div>
              <div className="text-xs text-zinc-400 uppercase tracking-[0.2em]">Magazzino</div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Accesso operatori</div>
          <h2 className="font-display text-4xl font-black uppercase mb-8 tracking-tight">Login</h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                className="gm-input" required autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
                className="gm-input" required
              />
            </div>
            {err && (
              <div data-testid="login-error" className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm p-3 rounded-sm">
                {err}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit" disabled={busy}
              className="gm-btn-primary w-full h-14 flex items-center justify-center gap-2 rounded-sm"
            >
              <ScanBarcode size={20} />
              {busy ? "Accesso in corso..." : "Entra nel sistema"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
